"use client";

import { Bars2Icon } from "@heroicons/react/24/outline";
import { CiCircleQuestion, CiLogout, CiSettings } from "react-icons/ci";
import { MdOutlineAdminPanelSettings } from "react-icons/md";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";

import { hardSignOut } from "@/lib/auth/clientSignOut";
import { EASE_PREMIUM, DURATION } from "@/lib/motion";

/**
 * The avatar and its menu. Everything reachable from here hangs off the account
 * rather than the studio, which is why none of it lives in the nav manifest.
 */
export default function AuthButton({ userData, session, createLocaleUrl }) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowMenu(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowMenu(false);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Close the menu on any click outside it.
    const handleDocumentClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  const itemVariants = {
    open: {
      opacity: 1,
      y: 0,
      transition: { duration: DURATION.fast, ease: EASE_PREMIUM },
    },
    closed: { opacity: 0, y: -4, transition: { duration: 0.1 } },
  };

  const menuItems = [
    {
      href: createLocaleUrl("/settings"),
      label: "Settings",
      show: !!userData?.workspaceId,
      icon: CiSettings,
    },
    {
      href: createLocaleUrl("/help"),
      label: "Help Center",
      show: true,
      icon: CiCircleQuestion,
    },

    // The superuser console is hidden while impersonating: the JWT keeps
    // superUser:true, so the person being impersonated would otherwise see it.
    ...(userData?.superUser && !session?.user?.isImpersonated
      ? [
          {
            href: createLocaleUrl("/admin/superUser"),
            label: "SuperUser Administration & Tools",
            show: true,
            icon: MdOutlineAdminPanelSettings,
            isSuperUserItem: true,
          },
        ]
      : []),
  ];

  if (!userData) return null;

  return (
    <div className="relative" ref={menuRef}>
      <motion.button
        type="button"
        whileTap={{ scale: 0.97 }}
        aria-haspopup="menu"
        aria-expanded={showMenu}
        className="flex items-center gap-1 px-1.5 py-1 font-semibold text-gray-800 rounded md:gap-2 md:px-2"
        onClick={() => setShowMenu(!showMenu)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {userData.image ? (
          <Image
            src={`${userData?.image}`}
            className="object-cover rounded-full"
            width={28}
            height={28}
            alt="profile picture"
          />
        ) : (
          <div className="inline-flex items-center justify-center text-white rounded-full w-9 h-9 bg-gray-700">
            <p className="mx-auto my-auto w-fit h-fit">
              {userData.name ? (
                userData.name[0]
              ) : (
                <Bars2Icon className="w-6 h-6 text-white" />
              )}
            </p>
          </div>
        )}
        <span className="hidden max-w-[9rem] truncate md:inline">
          {userData.handle || userData.name}
        </span>
        <motion.div
          variants={{
            open: { rotate: 180 },
            closed: { rotate: 0 },
          }}
          transition={{ duration: 0.2, ease: EASE_PREMIUM }}
          style={{ originY: 0.55 }}
          animate={showMenu ? "open" : "closed"}
        >
          <svg width="10" height="10" viewBox="0 0 20 20">
            <path
              d="M0 7 L 20 7 L 10 16"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>
      </motion.button>
      <motion.div
        variants={{
          open: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              duration: DURATION.base,
              ease: EASE_PREMIUM,
              staggerChildren: 0.03,
            },
          },
          closed: {
            opacity: 0,
            y: -6,
            scale: 0.98,
            transition: { duration: DURATION.fast, ease: EASE_PREMIUM },
          },
        }}
        initial={false}
        animate={showMenu ? "open" : "closed"}
        role="menu"
        // The panel is always mounted so it can animate, and opacity alone
        // hides it from eyes but not from screen readers or the tab order.
        aria-hidden={!showMenu}
        inert={!showMenu}
        style={{
          transformOrigin: "top right",
          pointerEvents: showMenu ? "auto" : "none",
        }}
        className="absolute right-0 top-full z-[60] mt-2 w-72 list-none rounded-xl border border-stone-200/80 bg-white p-2 shadow-[0_8px_28px_-12px_rgba(0,0,0,0.18)]"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {menuItems.map(
          (item) =>
            item.show && (
              <motion.div key={item.href} variants={itemVariants}>
                <Link
                  href={item.href}
                  role="menuitem"
                  className={`flex items-center rounded-full px-4 py-2 m-2 text-sm text-left transition-colors ${
                    item.isSuperUserItem
                      ? "text-orange-600 hover:bg-orange-50"
                      : "text-gray-700 hover:bg-brand-50 hover:text-brand-800"
                  }`}
                  onClick={() => setShowMenu(false)}
                >
                  <item.icon
                    className={`inline-block mr-3 w-5 h-5 ${
                      item.isSuperUserItem ? "text-orange-600" : ""
                    }`}
                  />
                  {item.isSuperUserItem && (
                    <span className="mr-2 text-xs">⚡</span>
                  )}
                  <span>{item.label}</span>
                </Link>
              </motion.div>
            ),
        )}
        {/* A button, not a div: signing out has to be reachable by keyboard. */}
        <motion.div variants={itemVariants} className="m-2">
          <button
            type="button"
            role="menuitem"
            className="flex w-full cursor-pointer items-center rounded-full px-4 py-2 text-sm text-left text-gray-700 transition-colors hover:bg-brand-50 hover:text-brand-800"
            onClick={() => hardSignOut("/")}
          >
            <CiLogout className="inline-block w-5 h-5 mr-3" />
            <span>Sign out</span>
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
